import { lazy, Suspense, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, CircleCheck, LogOut, MailQuestion, Network, Plus, UserPlus, X } from 'lucide-react';
import {
  CAPABILITY_EXCEPTION_REASON,
  CAPABILITY_LABELS,
  ROLE_CAPABILITIES,
  USER_ROLES,
  inviteNeedsReauth,
  isSensitiveCapability,
  sensitiveAdditions,
  type CapabilityName,
  type Department,
  type SystemMailDelivery,
  type UserListItem,
  type UserRole,
} from '@castinflow/shared';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  ConfirmDialog,
  DataTable,
  DensityProvider,
  EmptyState,
  Field,
  Input,
  PageContainer,
  PageHeader,
  Select,
  Skeleton,
  Textarea,
  useToast,
  type BadgeTone,
  type Column,
} from '@castinflow/ui';
import { ApiError, apiFetch } from '../lib/api';
import { apiMutate, useSession } from '../lib/session';
import { usePageMeta } from '../shell/page-meta';
import { planErrorText } from '../lib/plan-error';
import { SYSTEM_MAIL_BADGE_TONE, inviteToastTone } from '../lib/system-mail';
import { ROLE_LABEL, USER_STATUS_LABEL, tr } from '../i18n/tr';
import { QueryErrorState, UnauthorizedPage } from './StatePages';
import { MetricLinkCard, type MetricTone } from '../framework/common/metric-link';
import { RoleGuideCard, roleScopeChange } from '../framework/account/role-guide';
import {
  ORG_DEPT_PARAM,
  ORG_STATUS_FILTERS,
  ORG_STATUS_PARAM,
  departmentTreeOrder,
  orgF1Text as F,
  parseStatusFilter,
  type UserStatusFilter,
} from '../framework/account/org-strings';
import { usePlanUsageForSeats } from '../framework/plan/plan-context';
import { createCapabilityRequest } from '../framework/account/capability-requests';
import { ReauthFields } from '../framework/lifecycle/lifecycle-parts';

/** H2: rol değişikliğinin YENİ geçerli kıldığı hassas yetkiler (boşsa doğrudan uygulanır; sunucu aynı kuralla denetler) */
function roleSensitiveOf(u: UserListItem, role: UserRole): CapabilityName[] {
  if (u.isOwner) return [];
  return sensitiveAdditions(
    { role: u.role, override: u.capabilities },
    { role, override: u.capabilities },
  );
}

// Departman ayarları çerçeve metinlerini (kayıt türü adları) kullanır: tembel yüklenir, ana pakete girmez (check-web-bundle)
const DepartmentSettingsDialog = lazy(() =>
  import('./DepartmentSettingsDialog').then((m) => ({ default: m.DepartmentSettingsDialog })),
);

const STATUS_TONE: Record<UserListItem['status'], BadgeTone> = {
  invited: 'info',
  active: 'success',
  suspended: 'warning',
  left: 'neutral',
};

const STATUS_METRIC: Record<UserStatusFilter, { icon: ReactNode; tone: MetricTone }> = {
  active: { icon: <CircleCheck size={18} />, tone: 'success' },
  invited: { icon: <MailQuestion size={18} />, tone: 'info' },
  suspended: { icon: <Ban size={18} />, tone: 'warning' },
  left: { icon: <LogOut size={18} />, tone: 'neutral' },
};

const EMPTY_INVITE = { email: '', firstName: '', lastName: '', role: 'standard' as UserRole };

/** "Daveti yeniden gönder" reddinin kullanıcıya gösterilecek metni (sunucunun ileti metni gösterilmez) */
function resendErrorText(e: unknown): string {
  if (e instanceof ApiError) {
    const reason = (e.details as { reason?: string } | undefined)?.reason;
    if (e.code === 'RATE_LIMITED') return tr.org.inviteResendRateLimited;
    if (reason === 'invite_already_accepted') return tr.org.inviteResendAccepted;
    if (reason === 'grant_exceeds_actor') return tr.org.inviteResendForbidden;
    if (reason === 'sensitive_invite_owner_only') return tr.org.inviteSensitiveOwnerOnly;
    if (reason === 'reauth_required' || e.code === 'UNAUTHENTICATED')
      return tr.org.inviteReauthFailed;
  }
  return tr.org.inviteResendFailed;
}

/** K1: yönetici davetinin yeniden kimlik doğrulama gövdesi (değerler yalnız bileşenin yerel durumunda tutulur) */
const EMPTY_REAUTH = { password: '', totpCode: '' };
function reauthBody(r: { password: string; totpCode: string }) {
  return { password: r.password, ...(r.totpCode.length === 6 ? { totpCode: r.totpCode } : {}) };
}

/** Yöneticinin onayını bekleyen rol/durum değişikliği (istek yalnız onaydan sonra gider) */
type UserAction =
  | { kind: 'role'; user: UserListItem; role: UserRole }
  | { kind: 'suspend'; user: UserListItem }
  | { kind: 'reactivate'; user: UserListItem }
  // v3.1 A3 (K-M7-44.9): "Ayrıldı yap" (ardından isteğe bağlı devir sihirbazı) ve bekleyen daveti iptal
  | { kind: 'leave'; user: UserListItem }
  | { kind: 'cancelInvite'; user: UserListItem };

const fullName = (u: UserListItem) => `${u.firstName} ${u.lastName}`;

const USER_ACTION_REASONS = [
  'owner_protected',
  'self_change',
  'target_outranks_actor',
  'grant_exceeds_actor',
  'invite_pending',
  'invite_cancelled',
  'invite_already_accepted',
  'user_not_invited',
  'request_pending',
  'capability_exceptions_changed',
  'sensitive_approval_required',
] as const;

/** Rol/durum değişikliği reddinin kullanıcıya gösterilecek metni (sunucunun ileti metni gösterilmez; plan tavanı hariç) */
function userActionErrorText(e: unknown): string {
  const plan = planErrorText(e);
  if (plan !== null) return plan;
  if (e instanceof ApiError) {
    const reason = (e.details as { reason?: string } | undefined)?.reason;
    const known = USER_ACTION_REASONS.find((r) => r === reason);
    if (known !== undefined) return tr.org.userActionError[known];
    if (e.code === 'NOT_FOUND') return tr.org.userActionError.notFound;
    if (e.code === 'FORBIDDEN') return tr.org.userActionError.forbidden;
  }
  return tr.org.userActionError.failed;
}

/** Kullanıcılar ve departmanlar (WP 1.4 yönetimi; WP 2.7 liste şablonu ve durum yüzeyleriyle). */
export function OrgPage() {
  const me = useSession((s) => s.me);
  const canManage = me?.capabilities.includes('users.manage') ?? false;
  if (!canManage) return <UnauthorizedPage title={tr.org.title} />;
  return <OrgContent />;
}

function OrgContent() {
  usePageMeta(tr.org.title, [{ label: tr.nav.administration }]);
  // Yeniden gönderim davet ile aynı yetkiyi ister (`users.invite`); yetkisi olmayana düğme gösterilmez (sınır API'dedir)
  const canInvite = useSession((s) => s.me?.capabilities.includes('users.invite') ?? false);
  const [resendTarget, setResendTarget] = useState<UserListItem | null>(null);
  // Rol/durum eylemleri sayfanın yetkisiyle (`users.manage`) aynıdır. Sahibin ve oturum sahibinin kendi satırında eylem
  // gösterilmez; sınır API'dedir (gizlenen düğme güvenlik değildir): sunucu bu ikisini ve yetki tavanını ayrıca reddeder.
  const meId = useSession((s) => s.me?.user.id);
  // F14: demo alanında davet kapalı (sunucu reddeder); form bunu baştan söyler ve gönderimi kapatır
  const isDemo = useSession((s) => s.me?.tenant.isDemo === true);
  const [userAction, setUserAction] = useState<UserAction | null>(null);
  // H2: hassas yetki ekleyen rol değişikliğinin gerekçesi (talep)
  const [roleReason, setRoleReason] = useState('');
  const [roleReasonError, setRoleReasonError] = useState(false);
  const isOwnerMe = useSession((s) => s.me?.user.isOwner === true);
  // H2: hassas yetki taşıyan role yalnız sahip davet eder (sunucu da reddeder)
  const inviteSensitiveFor = (role: UserRole) =>
    !isOwnerMe && ROLE_CAPABILITIES[role].some(isSensitiveCapability);
  // "Ayrıldı yap" sonrası devir sihirbazı (varsayılan açık: ayrılan kullanıcının kayıtları sahipsiz kalmasın)
  const [openHandover, setOpenHandover] = useState(true);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const users = useQuery({
    queryKey: ['users'],
    queryFn: () => apiFetch<{ users: UserListItem[] }>('/users'),
  });
  const depts = useQuery({
    queryKey: ['departments'],
    queryFn: () => apiFetch<{ departments: Department[] }>('/departments'),
  });
  const [deptName, setDeptName] = useState('');
  // H2: sahip olmayan yöneticinin varsayılan davet rolü hassas yetki taşımayan "Sınırlı"dır (hassas role yalnız sahip davet eder)
  const emptyInvite = isOwnerMe ? EMPTY_INVITE : { ...EMPTY_INVITE, role: 'limited' as UserRole };
  const [invite, setInvite] = useState(emptyInvite);
  const inviteSensitive = inviteSensitiveFor(invite.role);
  // K1: sahip yönetici (Sistem yöneticisi) davet ederken mevcut yeniden kimlik doğrulaması (parola + açıksa kod) istenir
  const [inviteReauth, setInviteReauth] = useState(EMPTY_REAUTH);
  const inviteReauthNeeded = isOwnerMe && inviteNeedsReauth(invite.role);
  const [resendReauth, setResendReauth] = useState(EMPTY_REAUTH);
  // Departman paylaşım/onay ayarları ayrı yetkidir (`departments.manage`; API aynı yetkiyi ister)
  const canManageDepts = useSession(
    (s) => s.me?.capabilities.includes('departments.manage') ?? false,
  );
  const [settingsDept, setSettingsDept] = useState<Department | null>(null);

  const createDept = useMutation({
    mutationFn: (name: string) => apiMutate<Department>('/departments', 'POST', { name }),
    onSuccess: (d) => {
      setDeptName('');
      toast({ title: tr.org.departmentAdded(d.name) });
      void qc.invalidateQueries({ queryKey: ['departments'] });
    },
    onError: () => {
      toast({ tone: 'danger', title: tr.org.departmentFailed });
    },
  });
  const sendInvite = useMutation({
    // `mail` alanı M7 T2 ile gelir; alanı yayımlamayan önceki sunucu sürümünde (renk geçişi) yoktur
    mutationFn: (body: typeof invite & { reauth?: { password: string; totpCode?: string } }) =>
      apiMutate<{ userId: string; mail?: SystemMailDelivery }>('/auth/invite', 'POST', body),
    onSuccess: (out) => {
      setInvite(emptyInvite);
      setInviteReauth(EMPTY_REAUTH);
      // Davet kaydı oluştu; iletinin gönderildiği iddia edilmez — o andaki posta durumu not olarak yazılır
      const status = out.mail?.status;
      toast({
        tone: inviteToastTone(status),
        title: tr.org.inviteCreated,
        ...(status === undefined ? {} : { description: tr.org.inviteMailNote[status] }),
        testId: 'org-msg',
      });
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (e) => {
      // M6 WP 6.1: kullanıcı tavanı dolduysa sunucunun açıklayıcı iletisi gösterilir; demo alanı reddi kendi iletisiyle (F14)
      const why =
        e instanceof ApiError ? (e.details as { reason?: string } | undefined)?.reason : undefined;
      toast({
        tone: 'danger',
        title:
          planErrorText(e) ??
          (why === 'demo_workspace'
            ? tr.org.inviteDemoRefused
            : why === 'sensitive_invite_owner_only'
              ? tr.org.inviteSensitiveOwnerOnly
              : why === 'reauth_required' || (e instanceof ApiError && e.code === 'UNAUTHENTICATED')
                ? tr.org.inviteReauthFailed
                : tr.org.inviteFailed),
        testId: 'org-msg',
      });
    },
  });
  const resendInvite = useMutation({
    mutationFn: (x: { userId: string; reauth?: { password: string; totpCode?: string } }) =>
      apiMutate<{ userId: string; mail?: SystemMailDelivery }>('/auth/invite/resend', 'POST', x),
    onSuccess: (out) => {
      setResendTarget(null);
      setResendReauth(EMPTY_REAUTH);
      // Yeni davet kaydı oluştu, eski bağlantı geçersiz; iletinin gönderildiği yine iddia edilmez
      const status = out.mail?.status;
      toast({
        tone: inviteToastTone(status),
        title: tr.org.inviteResent,
        ...(status === undefined ? {} : { description: tr.org.inviteMailNote[status] }),
        testId: 'org-msg',
      });
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (e) => {
      setResendTarget(null);
      setResendReauth(EMPTY_REAUTH);
      toast({ tone: 'danger', title: resendErrorText(e), testId: 'org-msg' });
      // Davet bu arada kabul edilmiş olabilir: liste yeniden okunur
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
  const setUserDept = useMutation({
    mutationFn: ({ id, deptId }: { id: string; deptId: string | null }) =>
      apiMutate<UserListItem>(`/users/${id}`, 'PATCH', { deptId }),
    onSuccess: () => {
      toast({ title: tr.org.departmentChanged });
      void qc.invalidateQueries({ queryKey: ['users'] });
      void qc.invalidateQueries({ queryKey: ['departments'] });
    },
    onError: () => {
      toast({ tone: 'danger', title: tr.org.departmentChangeFailed });
    },
  });

  const applyUserAction = useMutation({
    mutationFn: async (a: UserAction): Promise<UserRole | 'requested'> => {
      if (a.kind === 'cancelInvite') {
        await apiMutate<undefined>('/auth/invite/cancel', 'POST', { userId: a.user.id });
        return a.user.role;
      }
      // H2: hassas yetki ekleyen rol değişikliği talep olarak gider (başka bir uygun yetkili onaylar)
      if (a.kind === 'role' && roleSensitiveOf(a.user, a.role).length > 0) {
        await createCapabilityRequest(a.user.id, {
          role: a.role,
          reason: roleReason.trim(),
          expected: { role: a.user.role, override: a.user.capabilities },
        });
        return 'requested';
      }
      const u = await apiMutate<UserListItem>(
        `/users/${a.user.id}`,
        'PATCH',
        a.kind === 'role'
          ? { role: a.role }
          : {
              status: a.kind === 'suspend' ? 'suspended' : a.kind === 'leave' ? 'left' : 'active',
            },
      );
      return u.role;
    },
    onSuccess: (role, a) => {
      setUserAction(null);
      setRoleReason('');
      const name = fullName(a.user);
      if (role === 'requested' && a.kind === 'role') {
        toast({ title: tr.org.roleRequested(name, ROLE_LABEL[a.role]), testId: 'org-msg' });
        void qc.invalidateQueries({ queryKey: ['users'] });
        void qc.invalidateQueries({ queryKey: ['capability-requests'] });
        return;
      }
      if (role === 'requested') return;
      toast({
        title:
          a.kind === 'role'
            ? tr.org.roleChanged(name, ROLE_LABEL[role])
            : a.kind === 'suspend'
              ? tr.org.suspended(name)
              : a.kind === 'leave'
                ? tr.org.left(name)
                : a.kind === 'cancelInvite'
                  ? tr.org.inviteCancelled(name)
                  : tr.org.reactivated(name),
        testId: 'org-msg',
      });
      void qc.invalidateQueries({ queryKey: ['users'] });
      if (a.kind === 'leave' && openHandover) navigate(`/users/${a.user.id}/handover`);
    },
    onError: (e) => {
      setUserAction(null);
      setRoleReason('');
      toast({ tone: 'danger', title: userActionErrorText(e), testId: 'org-msg' });
      // Kullanıcı bu arada başkasınca değiştirilmiş olabilir: liste yeniden okunur
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
  });

  function onDept(e: FormEvent) {
    e.preventDefault();
    if (deptName.trim().length > 0) createDept.mutate(deptName.trim());
  }
  function onInvite(e: FormEvent) {
    e.preventDefault();
    sendInvite.mutate(
      inviteReauthNeeded ? { ...invite, reauth: reauthBody(inviteReauth) } : invite,
    );
  }
  // K1: iptal edilmiş yönetici davetini yeniden açmak yeni davettir (sahip + yeniden kimlik doğrulama)
  const resendReauthNeeded =
    resendTarget !== null &&
    resendTarget.inviteCancelled === true &&
    inviteNeedsReauth(resendTarget.role);

  const departments = useMemo(
    () => departmentTreeOrder(depts.data?.departments ?? []),
    [depts.data?.departments],
  );
  // F1: durum/departman süzgeci adreste (`?durum=`, `?departman=`); ölçü kartının sayısı = süzülmüş tablo satırı (aynı liste)
  const [search, setSearch] = useSearchParams();
  const statusFilter = parseStatusFilter(search.get(ORG_STATUS_PARAM));
  const deptFilter = search.get(ORG_DEPT_PARAM);
  const allUsers = users.data?.users ?? [];
  const statusCount = (st: UserStatusFilter) => allUsers.filter((u) => u.status === st).length;
  const deptUserCount = (id: string) => allUsers.filter((u) => u.deptId === id).length;
  const rows = allUsers.filter(
    (u) =>
      (statusFilter === null || u.status === statusFilter) &&
      (deptFilter === null || u.deptId === deptFilter),
  );
  const filterLabel =
    statusFilter !== null
      ? F.metric[statusFilter]
      : deptFilter !== null
        ? F.filterDept(departments.find((d) => d.id === deptFilter)?.name ?? F.filterUnknownDept)
        : null;
  // Kullanıcı tavanı (yalnız ayar yetkisinde okunur; sunucu tavanı yine kendisi uygular)
  const canSeeUsage = useSession((s) => s.me?.capabilities.includes('settings.manage') ?? false);
  const seats = usePlanUsageForSeats(canSeeUsage);
  const columns: Array<Column<UserListItem>> = [
    {
      key: 'name',
      header: tr.org.colName,
      priority: 'high',
      cell: (u) => (
        <span className="inline-flex min-w-0 items-center gap-2">
          <Link
            to={`/users/${u.id}`}
            className="truncate font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
            data-testid="org-user-link"
          >
            {u.firstName} {u.lastName}
          </Link>
          {u.isOwner ? <Badge tone="primary">{tr.org.ownerBadge}</Badge> : null}
        </span>
      ),
    },
    {
      key: 'email',
      header: tr.org.colEmail,
      priority: 'low',
      truncate: true,
      cell: (u) => u.email,
    },
    {
      key: 'role',
      header: tr.org.colRole,
      priority: 'high',
      cell: (u) =>
        u.isOwner || u.id === meId ? (
          <span
            title={u.isOwner ? tr.org.lockedOwner : tr.org.lockedSelf}
            data-testid={`user-role-locked-${u.email}`}
          >
            {ROLE_LABEL[u.role]}
          </span>
        ) : (
          // Seçim istek göndermez: onay penceresini açar. Liste yenilenene dek gösterilen değer sunucudaki roldür
          <Select
            compact
            aria-label={tr.org.roleFor(fullName(u))}
            value={u.role}
            className="min-w-[9rem]"
            disabled={applyUserAction.isPending}
            onChange={(e) => {
              const role = e.target.value as UserRole;
              if (role !== u.role) setUserAction({ kind: 'role', user: u, role });
            }}
            data-testid={`user-role-${u.email}`}
          >
            {USER_ROLES.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r]}
              </option>
            ))}
          </Select>
        ),
    },
    {
      key: 'status',
      header: tr.org.colStatus,
      priority: 'high',
      cell: (u) => (
        <span className="inline-flex flex-wrap items-center gap-1">
          <Badge tone={STATUS_TONE[u.status]}>{USER_STATUS_LABEL[u.status]}</Badge>
          {/* M7 T2: davet iletisinin posta durumu (kuyrukta / sağlayıcı kabul etti / gönderilemedi / yapılandırılmamış) */}
          {u.status === 'invited' && u.inviteMail ? (
            <span
              title={tr.org.inviteMailHint[u.inviteMail.status]}
              data-testid={`invite-mail-${u.email}`}
              data-mail-status={u.inviteMail.status}
            >
              <Badge tone={SYSTEM_MAIL_BADGE_TONE[u.inviteMail.status]}>
                {tr.org.inviteMailLabel}: {tr.org.inviteMailStatus[u.inviteMail.status]}
              </Badge>
            </span>
          ) : null}
          {u.inviteCancelled === true ? (
            <Badge tone="neutral">{tr.org.inviteCancelledBadge}</Badge>
          ) : null}
          {/* Yeniden gönderim yöneticinin açık eylemidir (otomatik yeniden gönderim yok); onay diyaloğu sonucu anlatır.
              Daveti iptal edilmiş kullanıcıya da (aynı adres) davet yeniden gönderilir */}
          {(u.status === 'invited' || u.inviteCancelled === true) && canInvite ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setResendTarget(u);
              }}
              data-testid={`invite-resend-${u.email}`}
            >
              {tr.org.inviteResend}
            </Button>
          ) : null}
          {u.status === 'invited' && canInvite ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setUserAction({ kind: 'cancelInvite', user: u });
              }}
              data-testid={`invite-cancel-${u.email}`}
            >
              {tr.org.inviteCancel}
            </Button>
          ) : null}
          {/* Askıya alma yalnız etkin kullanıcıda; davet bekleyen kullanıcı daveti kabul ederek etkinleşir */}
          {u.status === 'active' && !u.isOwner && u.id !== meId ? (
            <Button
              size="sm"
              onClick={() => {
                setUserAction({ kind: 'suspend', user: u });
              }}
              data-testid={`user-suspend-${u.email}`}
            >
              {tr.org.suspend}
            </Button>
          ) : null}
          {(u.status === 'suspended' || u.status === 'left') &&
          u.inviteCancelled !== true &&
          !u.isOwner &&
          u.id !== meId ? (
            <Button
              size="sm"
              onClick={() => {
                setUserAction({ kind: 'reactivate', user: u });
              }}
              data-testid={`user-reactivate-${u.email}`}
            >
              {tr.org.reactivate}
            </Button>
          ) : null}
          {/* v3.1 A3: ayrılış — oturumlar ve kişisel API anahtarları kapanır; kayıtlar devir sihirbazıyla devredilir */}
          {(u.status === 'active' || u.status === 'suspended') && !u.isOwner && u.id !== meId ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setOpenHandover(true);
                setUserAction({ kind: 'leave', user: u });
              }}
              data-testid={`user-leave-${u.email}`}
            >
              {tr.org.leave}
            </Button>
          ) : null}
          {u.status === 'left' && u.inviteCancelled !== true && !u.isOwner && u.id !== meId ? (
            <Link
              to={`/users/${u.id}/handover`}
              className="text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
              data-testid={`user-handover-${u.email}`}
            >
              {tr.org.handover}
            </Link>
          ) : null}
        </span>
      ),
    },
    {
      key: 'dept',
      header: tr.org.colDepartment,
      widthClass: 'w-48',
      priority: 'high',
      cell: (u) => (
        <Select
          compact
          aria-label={tr.org.departmentFor(`${u.firstName} ${u.lastName}`)}
          value={u.deptId ?? ''}
          className="min-w-[9rem]"
          disabled={setUserDept.isPending}
          onChange={(e) => {
            setUserDept.mutate({ id: u.id, deptId: e.target.value === '' ? null : e.target.value });
          }}
          data-testid={`user-dept-${u.email}`}
        >
          <option value="">{tr.org.noDepartment}</option>
          {departments.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </Select>
      ),
    },
  ];

  return (
    <PageContainer>
      <PageHeader title={tr.org.title} description={tr.org.description} />
      <ConfirmDialog
        open={resendTarget !== null}
        title={
          resendTarget === null
            ? tr.org.inviteResend
            : tr.org.inviteResendTitle(`${resendTarget.firstName} ${resendTarget.lastName}`)
        }
        description={
          <div className="space-y-3">
            <p>
              {resendTarget?.inviteMail?.status === 'uncertain'
                ? `${tr.org.inviteResendBody} ${tr.org.inviteResendUncertain}`
                : tr.org.inviteResendBody}
            </p>
            {resendReauthNeeded ? (
              isOwnerMe ? (
                <>
                  <p>{tr.org.inviteAdminReauthHint}</p>
                  <ReauthFields
                    password={resendReauth.password}
                    totpCode={resendReauth.totpCode}
                    onPassword={(v) => {
                      setResendReauth({ ...resendReauth, password: v });
                    }}
                    onTotp={(v) => {
                      setResendReauth({ ...resendReauth, totpCode: v });
                    }}
                    idPrefix="invite-resend-reauth"
                    passwordHint={tr.org.inviteReauthPasswordHint}
                  />
                </>
              ) : (
                <p>{tr.org.inviteSensitiveOwnerOnly}</p>
              )
            ) : null}
          </div>
        }
        confirmLabel={tr.org.inviteResendConfirm}
        busy={resendInvite.isPending}
        onCancel={() => {
          setResendTarget(null);
          setResendReauth(EMPTY_REAUTH);
        }}
        onConfirm={() => {
          if (resendReauthNeeded && isOwnerMe && resendReauth.password.length === 0) {
            toast({ tone: 'warning', title: tr.org.inviteReauthFailed, testId: 'org-msg' });
            return;
          }
          if (resendTarget !== null)
            resendInvite.mutate(
              resendReauthNeeded && isOwnerMe
                ? { userId: resendTarget.id, reauth: reauthBody(resendReauth) }
                : { userId: resendTarget.id },
            );
        }}
        testId="invite-resend-dialog"
      />
      <ConfirmDialog
        open={userAction !== null}
        title={
          userAction === null
            ? ''
            : userAction.kind === 'role'
              ? tr.org.roleChangeTitle(fullName(userAction.user))
              : userAction.kind === 'suspend'
                ? tr.org.suspendTitle(fullName(userAction.user))
                : userAction.kind === 'leave'
                  ? tr.org.leaveTitle(fullName(userAction.user))
                  : userAction.kind === 'cancelInvite'
                    ? tr.org.inviteCancelTitle(fullName(userAction.user))
                    : tr.org.reactivateTitle(fullName(userAction.user))
        }
        description={
          userAction === null ? (
            ''
          ) : userAction.kind === 'role' ? (
            <div className="space-y-2">
              <p>
                {tr.org.roleChangeBody(
                  ROLE_LABEL[userAction.user.role],
                  ROLE_LABEL[userAction.role],
                )}
              </p>
              <p data-testid="role-change-scope">
                {roleScopeChange(userAction.user.role, userAction.role)}
              </p>
              {roleSensitiveOf(userAction.user, userAction.role).length > 0 ? (
                <div className="space-y-2" data-testid="role-change-sensitive">
                  <Alert
                    tone="warning"
                    title={tr.org.roleSensitive(
                      roleSensitiveOf(userAction.user, userAction.role)
                        .map((c) => CAPABILITY_LABELS[c])
                        .join(', '),
                    )}
                  />
                  <Field
                    label={tr.org.roleReason}
                    description={tr.org.roleReasonHint}
                    required
                    {...(roleReasonError ? { error: tr.org.roleReasonShort } : {})}
                  >
                    <Textarea
                      value={roleReason}
                      rows={2}
                      maxLength={500}
                      onChange={(e) => {
                        setRoleReason(e.target.value);
                        setRoleReasonError(false);
                      }}
                      data-testid="role-change-reason"
                    />
                  </Field>
                </div>
              ) : null}
            </div>
          ) : userAction.kind === 'suspend' ? (
            tr.org.suspendBody
          ) : userAction.kind === 'leave' ? (
            <div className="space-y-3">
              <p>{tr.org.leaveBody}</p>
              <Checkbox
                label={tr.org.leaveOpenHandover}
                checked={openHandover}
                onChange={(e) => {
                  setOpenHandover(e.target.checked);
                }}
                data-testid="user-leave-handover"
              />
            </div>
          ) : userAction.kind === 'cancelInvite' ? (
            tr.org.inviteCancelBody
          ) : (
            tr.org.reactivateBody(ROLE_LABEL[userAction.user.role])
          )
        }
        confirmLabel={
          userAction?.kind === 'role'
            ? roleSensitiveOf(userAction.user, userAction.role).length > 0
              ? tr.org.roleRequestConfirm
              : tr.org.roleChangeConfirm
            : userAction?.kind === 'suspend'
              ? tr.org.suspendConfirm
              : userAction?.kind === 'leave'
                ? tr.org.leaveConfirm
                : userAction?.kind === 'cancelInvite'
                  ? tr.org.inviteCancelConfirm
                  : tr.org.reactivateConfirm
        }
        destructive={userAction?.kind === 'leave' || userAction?.kind === 'cancelInvite'}
        busy={applyUserAction.isPending}
        onCancel={() => {
          setUserAction(null);
          setRoleReason('');
          setRoleReasonError(false);
        }}
        onConfirm={() => {
          if (userAction === null) return;
          if (
            userAction.kind === 'role' &&
            roleSensitiveOf(userAction.user, userAction.role).length > 0 &&
            roleReason.trim().length < CAPABILITY_EXCEPTION_REASON.min
          ) {
            setRoleReasonError(true);
            return;
          }
          applyUserAction.mutate(userAction);
        }}
        testId="user-action-dialog"
      />
      <div
        className="grid grid-cols-1 gap-4 2xl:grid-cols-[minmax(0,1fr)_20rem]"
        data-testid="org-page"
      >
        <DensityProvider density="standard" className="min-w-0 space-y-3">
          {users.isSuccess ? (
            <section aria-label={F.metricsTitle}>
              <ul className="grid grid-cols-2 gap-2 lg:grid-cols-4" data-testid="org-metrics">
                {ORG_STATUS_FILTERS.map((st) => (
                  <li key={st} className="min-w-0">
                    <MetricLinkCard
                      to={`/org?${ORG_STATUS_PARAM}=${st}`}
                      label={F.metric[st]}
                      count={statusCount(st)}
                      exact
                      hint={F.metricHint[st]}
                      icon={STATUS_METRIC[st].icon}
                      tone={STATUS_METRIC[st].tone}
                      testId={`org-metric-${st}`}
                    />
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          {filterLabel !== null ? (
            <p
              className="flex flex-wrap items-center gap-2 text-sm text-fg-muted"
              role="status"
              data-testid="org-filter"
            >
              <span>{F.filterActive(filterLabel, rows.length)}</span>
              <Button
                variant="ghost"
                size="sm"
                leadingIcon={<X size={14} aria-hidden="true" />}
                onClick={() => {
                  setSearch({});
                }}
                data-testid="org-filter-clear"
              >
                {F.filterClear}
              </Button>
            </p>
          ) : null}
          <DataTable
            caption={tr.org.usersTable}
            captionHidden
            testId="user-list"
            columns={columns}
            rows={rows}
            rowKey={(u) => u.id}
            primaryKey="name"
            rowLabel={(u) => `${u.firstName} ${u.lastName}`}
            status={users.isPending ? 'loading' : users.isError ? 'error' : 'ready'}
            loadingRows={3}
            empty={
              <EmptyState
                headingLevel={3}
                title={tr.org.usersEmptyTitle}
                description={tr.org.usersEmptyBody}
              />
            }
            error={<QueryErrorState error={users.error} onRetry={() => void users.refetch()} />}
          />
        </DensityProvider>

        <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2 2xl:grid-cols-1">
          <Card as="section" aria-labelledby="invite-title">
            <CardHeader
              titleId="invite-title"
              title={tr.org.inviteTitle}
              icon={<UserPlus size={18} />}
              description={
                <span data-testid="invite-description">
                  {isDemo ? tr.org.inviteDemoNote : tr.org.inviteDescription}
                </span>
              }
            />
            <CardBody>
              {seats !== null && seats.limit !== null && seats.used >= seats.limit - 1 ? (
                <div className="mb-3" data-testid="invite-seats">
                  <Alert
                    tone={seats.used >= seats.limit ? 'warning' : 'info'}
                    title={F.seatTitle(seats.used, seats.limit)}
                  >
                    <p>
                      {seats.used >= seats.limit
                        ? F.seatFull
                        : F.seatNear(seats.limit - seats.used)}
                    </p>
                    <Link
                      to="/settings/plan"
                      className="inline-flex min-h-11 items-center text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                    >
                      {F.seatPlanLink}
                    </Link>
                  </Alert>
                </div>
              ) : null}
              <form onSubmit={onInvite} className="space-y-3" noValidate>
                <Field label={tr.org.colEmail} required>
                  <Input
                    type="email"
                    autoComplete="off"
                    value={invite.email}
                    onChange={(e) => {
                      setInvite({ ...invite, email: e.target.value });
                    }}
                    data-testid="invite-email"
                  />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label={tr.auth.firstName} required>
                    <Input
                      value={invite.firstName}
                      onChange={(e) => {
                        setInvite({ ...invite, firstName: e.target.value });
                      }}
                      data-testid="invite-first"
                    />
                  </Field>
                  <Field label={tr.auth.lastName} required>
                    <Input
                      value={invite.lastName}
                      onChange={(e) => {
                        setInvite({ ...invite, lastName: e.target.value });
                      }}
                      data-testid="invite-last"
                    />
                  </Field>
                </div>
                <Field
                  label={tr.org.colRole}
                  {...(inviteSensitive ? { description: tr.org.inviteSensitiveHint } : {})}
                >
                  <Select
                    value={invite.role}
                    onChange={(e) => {
                      setInvite({ ...invite, role: e.target.value as UserRole });
                    }}
                    data-testid="invite-role"
                  >
                    {USER_ROLES.map((r) => (
                      <option key={r} value={r}>
                        {ROLE_LABEL[r]}
                      </option>
                    ))}
                  </Select>
                </Field>
                {inviteReauthNeeded ? (
                  <div className="space-y-3" data-testid="invite-reauth">
                    <p className="text-sm text-fg-muted">{tr.org.inviteAdminReauthHint}</p>
                    <ReauthFields
                      password={inviteReauth.password}
                      totpCode={inviteReauth.totpCode}
                      onPassword={(v) => {
                        setInviteReauth({ ...inviteReauth, password: v });
                      }}
                      onTotp={(v) => {
                        setInviteReauth({ ...inviteReauth, totpCode: v });
                      }}
                      idPrefix="invite-reauth"
                      passwordHint={tr.org.inviteReauthPasswordHint}
                    />
                  </div>
                ) : null}
                <Button
                  type="submit"
                  variant="primary"
                  className="w-full"
                  loading={sendInvite.isPending}
                  disabled={
                    isDemo ||
                    inviteSensitive ||
                    (inviteReauthNeeded && inviteReauth.password.length === 0)
                  }
                  data-testid="invite-send"
                >
                  {tr.org.inviteSend}
                </Button>
              </form>
            </CardBody>
          </Card>

          <Card as="section" aria-labelledby="dept-title">
            <CardHeader
              titleId="dept-title"
              title={tr.org.departments}
              icon={<Network size={18} />}
            />
            <CardBody>
              <div data-testid="dept-list">
                {depts.isPending ? (
                  <div className="space-y-2" aria-busy="true">
                    <Skeleton className="h-3 w-2/3" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                ) : depts.isError ? (
                  <QueryErrorState error={depts.error} onRetry={() => void depts.refetch()} />
                ) : departments.length === 0 ? (
                  <EmptyState
                    headingLevel={3}
                    className="py-2 sm:py-2"
                    title={tr.org.departmentsEmptyTitle}
                    description={tr.org.departmentsEmptyBody}
                  />
                ) : (
                  <ul className="space-y-1 text-base">
                    {departments.map((d) => (
                      <li
                        key={d.id}
                        className="flex items-center gap-2"
                        style={{ paddingLeft: `${d.depth * 16}px` }}
                      >
                        <span className="min-w-0 flex-1 truncate">{d.name}</span>
                        <Link
                          to={`/org?${ORG_DEPT_PARAM}=${d.id}`}
                          className="inline-flex min-h-11 items-center px-1 text-sm tabular-nums text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                          aria-label={F.deptUserLink(d.name, deptUserCount(d.id))}
                          data-testid={`dept-users-${d.id}`}
                          data-count={deptUserCount(d.id)}
                        >
                          {F.deptUserCount(deptUserCount(d.id))}
                        </Link>
                        {canManageDepts ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={tr.org.deptSettings.openFor(d.name)}
                            onClick={() => {
                              setSettingsDept(d);
                            }}
                            data-testid={`dept-settings-${d.id}`}
                          >
                            {tr.org.deptSettings.open}
                          </Button>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
                {settingsDept ? (
                  <Suspense fallback={null}>
                    <DepartmentSettingsDialog
                      department={settingsDept}
                      departments={departments}
                      onClose={() => {
                        setSettingsDept(null);
                      }}
                    />
                  </Suspense>
                ) : null}
              </div>
              <form onSubmit={onDept} className="mt-4 space-y-2">
                <Field label={tr.org.newDepartment}>
                  <Input
                    value={deptName}
                    onChange={(e) => {
                      setDeptName(e.target.value);
                    }}
                    data-testid="dept-name"
                  />
                </Field>
                <Button
                  type="submit"
                  className="w-full"
                  leadingIcon={<Plus size={16} aria-hidden="true" />}
                  loading={createDept.isPending}
                  data-testid="dept-create"
                >
                  {tr.org.addDepartment}
                </Button>
              </form>
            </CardBody>
          </Card>

          <RoleGuideCard />
        </div>
      </div>
    </PageContainer>
  );
}
